import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cityData, cityImages } from "@/lib/cities-data"
import { serviceTypes } from "@/lib/form-options"
import { QuoteFormData } from "@/lib/validations/quote-form"
import { motion } from "framer-motion"
import { MapPin, Truck } from "lucide-react"
import { Control, Controller, FieldErrors, UseFormTrigger } from "react-hook-form"
import { FormFieldWrapper } from "./form-field-wrapper"
import Image from "next/image"

interface StepMoveDetailsProps {
  control: Control<QuoteFormData>
  errors: FieldErrors<QuoteFormData>
  trigger: UseFormTrigger<QuoteFormData> 
}

export function StepMoveDetails({ control, errors, trigger }: StepMoveDetailsProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className="grid grid-cols-1 md:grid-cols-3 gap-6"
    >
      <FormFieldWrapper
        icon={MapPin}
        iconColor="bg-gradient-to-br from-[#003DA5] to-blue-600"
        label="From"
        error={errors.from?.message}
        hoverBorderColor="group-hover:border-[#003DA5]/30"
      >
        <Controller
          name="from"
          control={control}
          render={({ field }) => (
            <Select value={field.value}
              onValueChange={(value) => {
                field.onChange(value)
                trigger(["from", "to"]) // 👈 revalidate both fields
              }}
            >
              <SelectTrigger aria-label="Select origin city">
                <SelectValue placeholder="Select origin city" />
              </SelectTrigger>
              <SelectContent>
                {cityData.map((city) => (
                  <SelectItem key={city.id} value={city.name}>
                    <div className="flex items-center gap-3">
                      <div className="relative w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-gray-200">
                        <Image
                          src={cityImages[city.slug] || "/images/cities/default.jpg"}
                          alt={city.name}
                          fill
                          className="object-cover"
                          sizes="32px"
                        />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{city.name}</span>
                        <span className="text-xs text-gray-500">{city.state}</span>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </FormFieldWrapper>

      <FormFieldWrapper
        icon={MapPin}
        iconColor="bg-gradient-to-br from-[#FF7A00] to-orange-600"
        label="To"
        error={errors.to?.message}
        hoverBorderColor="group-hover:border-[#FF7A00]/30"
      >
        <Controller
          name="to"
          control={control}
          render={({ field }) => (
            <Select value={field.value}
              onValueChange={(value) => {
                field.onChange(value)
                trigger(["from", "to"]) // 👈 triggers schema revalidation
              }}
            >
              <SelectTrigger aria-label="Select destination city">
                <SelectValue placeholder="Select destination" />
              </SelectTrigger>
              <SelectContent>
                {cityData.map((city) => (
                  <SelectItem key={city.id} value={city.name}>
                    <div className="flex items-center gap-3">
                      <div className="relative w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-gray-200">
                        <Image
                          src={cityImages[city.slug] || "/images/cities/default.jpg"}
                          alt={city.name}
                          fill
                          className="object-cover"
                          sizes="32px"
                        />
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{city.name}</span>
                        <span className="text-xs text-gray-500">{city.state}</span>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </FormFieldWrapper>

      <FormFieldWrapper
        icon={Truck}
        iconColor="bg-gradient-to-br from-purple-500 to-purple-600"
        label="Move Type"
        error={errors.moveType?.message}
        hoverBorderColor="group-hover:border-purple-500/30"
      >
        <Controller
          name="moveType"
          control={control}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger aria-label="Select move type">
                <SelectValue placeholder="Select move type" />
              </SelectTrigger>
              <SelectContent>
                {serviceTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </FormFieldWrapper>
    </motion.div>
  )
}
